/**
 * The worklist rows: compliance as countdowns, severity sort. One list, several
 * entry points — the branch tab, the patient page slice, the command center's
 * coverage panel all render this. Rows only: no assignees, no claim, no resolve;
 * a row clears when its cause clears.
 *
 * A row's only interaction is its own action: an unfilled visit's **Move within
 * window**, a canceled visit's **Rebook**, and
 * otherwise a link to the patient or the command center. Selecting a row in the
 * coverage panel narrows what the panel is looking at; it hands the row to nobody.
 */
import { useState } from "react";
import { Box, Button, Chip, Link as MuiLink, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useBranchClock } from "../../components/branchClock";
import { KIND_LABELS, type WorklistRow } from "../../engine/worklist";
import { useDemoSelector } from "../../store/useDemoStore";
import { MoveWithinWindowDialog } from "../episode/MoveWithinWindowDialog";
import { RebookDialog } from "./RebookDialog";

const SEVERITY: Record<1 | 2 | 3, { color: string; label: string }> = {
  3: { color: "#c62828", label: "now" },
  2: { color: "#ef6c00", label: "today" },
  1: { color: "#9e9e9e", label: "this week" },
};

export function SeverityDot({ severity }: { severity: 1 | 2 | 3 }) {
  return (
    <Tooltip title={`severity ${severity} · ${SEVERITY[severity].label}`}>
      <Box component="span" sx={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", bgcolor: SEVERITY[severity].color, mr: 1, verticalAlign: "middle" }} data-severity={severity} />
    </Tooltip>
  );
}

export function WorklistRows({
  rows,
  compact = false,
  showPatient = true,
  selectedId,
  onSelect,
}: {
  rows: WorklistRow[];
  compact?: boolean;
  showPatient?: boolean;
  /** The row the coverage panel is working (4.1); selection is a lens, never an assignment. */
  selectedId?: string;
  onSelect?: (row: WorklistRow) => void;
}) {
  const patientIds = useDemoSelector((s) => s.meta.bindings.patientIdsByRef);
  const roster = useDemoSelector((s) => s.roster);
  const clock = useBranchClock(useDemoSelector((s) => s.config.branch.timeZone));
  const [rebookFor, setRebookFor] = useState<string | undefined>(undefined);
  const [moveFor, setMoveFor] = useState<string | undefined>(undefined);
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing on the worklist.
      </Typography>
    );
  }
  return (
    <Table size="small" sx={{ "& td, & th": { py: compact ? 0.25 : 0.5, verticalAlign: "top" } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 36 }} />
          <TableCell>Item</TableCell>
          {showPatient && <TableCell>Patient</TableCell>}
          <TableCell>Clinician</TableCell>
          <TableCell>Countdown</TableCell>
          {!compact && <TableCell>Reason</TableCell>}
          <TableCell align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const patientId = r.patientRef ? patientIds[r.patientRef] : undefined;
          const clinician = r.clinicianHandle ? roster[r.clinicianHandle]?.displayName : undefined;
          return (
            <TableRow
              key={r.id}
              hover
              data-kind={r.kind}
              data-selected={selectedId === r.id ? "true" : undefined}
              selected={selectedId === r.id}
              onClick={onSelect ? () => onSelect(r) : undefined}
              sx={onSelect ? { cursor: "pointer" } : undefined}
            >
              <TableCell>
                <SeverityDot severity={r.severity} />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label={KIND_LABELS[r.kind]} />
                  {r.discipline && <Chip size="small" label={r.discipline} sx={{ height: 18, fontSize: 10.5 }} />}
                  {compact && (
                    <Typography variant="caption" color="text.secondary">
                      {clock.sentence(r.reason)}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              {showPatient && (
                <TableCell>
                  {patientId !== undefined ? (
                    <MuiLink component={RouterLink} to={`/patients/${patientId}`}>
                      {r.patientName ?? r.patientRef}
                    </MuiLink>
                  ) : (
                    r.patientName ?? r.patientRef ?? "—"
                  )}
                </TableCell>
              )}
              <TableCell>{clinician ?? "—"}</TableCell>
              <CountdownCell text={r.countdown === undefined ? undefined : clock.sentence(r.countdown)} />
              {!compact && (
                <TableCell>
                  <Typography variant="body2">{clock.sentence(r.reason)}</Typography>
                </TableCell>
              )}
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                {r.realVisitId !== undefined && (
                  <MuiLink component={RouterLink} to="/command" sx={{ mr: 1, fontSize: 12 }}>
                    command center
                  </MuiLink>
                )}
                {r.kind === "unfilled" && r.planVisitId && (
                  <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={() => setMoveFor(r.planVisitId)}>
                    Move within window
                  </Button>
                )}
                {r.kind === "canceledRebook" && r.patientRef && (
                  <Button size="small" variant="outlined" onClick={() => setRebookFor(r.patientRef)}>
                    Rebook
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
      {rebookFor && <RebookDialog patientRef={rebookFor} onClose={() => setRebookFor(undefined)} />}
      {moveFor && <MoveWithinWindowDialog planVisitId={moveFor} onClose={() => setMoveFor(undefined)} />}
    </Table>
  );
}

/** The countdown, red once it has run out. "overdue" is the engine's own word, read off the phrased line. */
function CountdownCell({ text }: { text?: string }) {
  return <TableCell sx={{ whiteSpace: "nowrap", color: text?.includes("overdue") ? "error.main" : undefined }}>{text ?? "—"}</TableCell>;
}
